/**
 * Skill API — 技能发布相关接口
 */

import { mutate } from "swr";
import { apiFetch } from "@/lib/api/client";

export interface CreateSkillRequest {
  name: string;
  description: string;
  skillKey?: string;
  version?: string;
  category?: string;
  tags?: string;
  level?: string;
  source?: string;
  author?: string;
  repoUrl?: string;
  archivePath?: string;
  directory?: string;
  isPaid?: number;
  price?: number;
  publisherType?: string;
  metadata?: Record<string, unknown>;
  content?: string;
  collectNum?: number;
  likeNum?: number;
}

export interface CreateSkillResponse {
  id: number;
  name: string;
}

export type UpdateSkillRequest = Partial<CreateSkillRequest> & {
  id: number;
};

export interface SkillVersionResp {
  id: number;
  skillKey: string;
  version: string;
  name: string;
  description: string;
  category: string;
  tags: string;
  level: string;
  directory: string;
  author: string;
  repoUrl: string;
  archivePath: string;
  source: string;
  isPaid: number;
  price: number;
  publisherType: string;
  createdAt: string;
}

export interface MySkillItem {
  id: number;
  skillKey: string;
  name: string;
  description: string;
  category: string;
  tags: string;
  version: string;
  downloadNum: number;
  collectNum: number;
  isPaid: number;
  price: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 获取当前用户已安装的 skill 列表
 * 注意：组件层请优先使用 `useMySkills()` hook (from lib/swr)
 */
export async function fetchMySkills(): Promise<MySkillItem[]> {
  const resp = await apiFetch<{ items?: MySkillItem[] }>(
    "/skill/api/v1/my/skills",
  );
  return resp.items ?? [];
}

/**
 * 获取当前用户已安装的 skill key 集合
 */
export async function fetchMySkillKeys(): Promise<Set<string>> {
  const items = await fetchMySkills();
  return new Set(items.map((item) => item.skillKey).filter(Boolean));
}

export function invalidateMySkillsCache() {
  return mutate("/skill/api/v1/my/skills");
}

/**
 * 创建技能
 */
export async function createSkill(
  req: CreateSkillRequest,
): Promise<CreateSkillResponse> {
  return apiFetch<CreateSkillResponse>("/admin/api/v1/skill/", {
    method: "POST",
    body: req,
  });
}

export async function updateSkill(req: UpdateSkillRequest): Promise<void> {
  await apiFetch("/admin/api/v1/skill/", {
    method: "PUT",
    body: req,
  });
}

/**
 * 通过压缩包访问地址解析 skill 信息
 * POST /admin/api/v1/skill/parse  { url: accessUrl }
 */
export async function parseSkillByUrl(
  accessUrl: string,
): Promise<SkillVersionResp> {
  return apiFetch<SkillVersionResp>("/admin/api/v1/skill/parse", {
    method: "POST",
    body: { zipUrl: accessUrl },
  });
}
